// src/main.tsx

// ← Native Face ID callback from iOS wrapper
;(window as any).onBiometricResult = async (success: boolean) => {
  if (success) {
    await performPasswordlessLogin()
  } else {
    alert("Biometric authentication failed or was canceled.")
  }
}

// ——————————————————————————————————————————————————
// iOS WKWebView geolocation stub (must run before any other code)
// ——————————————————————————————————————————————————
;(function() {
  const ua = navigator.userAgent || ''
  if (/iP(hone|od|ad)/.test(ua) && navigator.geolocation) {
    // Override the geolocation API so WKWebView never tries to open about:blank
    // @ts-ignore
    navigator.geolocation.getCurrentPosition = (
      success: PositionCallback,
      error?: PositionErrorCallback,
      options?: PositionOptions
    ) => {
      console.warn(
        '⚠️ navigator.geolocation.getCurrentPosition stubbed on iOS; using native event instead'
      )
      error?.({
        code: 1,
        message: 'Geolocation unsupported in WKWebView',
        PERMISSION_DENIED: 1,
        POSITION_UNAVAILABLE: 2,
        TIMEOUT: 3
      } as any)
    }
    // @ts-ignore
    navigator.geolocation.watchPosition = (
      success: PositionCallback,
      error?: PositionErrorCallback,
      options?: PositionOptions
    ) => {
      console.warn(
        '⚠️ navigator.geolocation.watchPosition stubbed on iOS; using native event instead'
      )
      return 0
    }
    // @ts-ignore
    navigator.geolocation.clearWatch = (_id: number) => {
      /* no-op */
    }
  }
})()

import React, { useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { performPasswordlessLogin } from './lib/supabase/auth'   // ← NEW import
import App from './App'
import './index.css'

// Function to update viewport height and safe area insets
function updateViewportHeight() {
  const vh = window.innerHeight * 0.01
  document.documentElement.style.setProperty('--vh', `${vh}px`)
  document.documentElement.style.setProperty(
    '--safe-bottom',
    `${window.visualViewport?.height
      ? window.visualViewport.height - window.innerHeight
      : 0}px`
  )
}

// Google Analytics measurement ID
const GA_MEASUREMENT_ID = 'G-E3V4GQTDSQ'

const Root = () => {
  useEffect(() => {
    // ✅ Show loading overlay during reload
    window.addEventListener('beforeunload', () => {
      document.body.classList.add('reloading')
    })

    // ✅ iOS WebView scroll fix
    const isIOSWebView = !!window.webkit?.messageHandlers
    if (isIOSWebView) {
      document.body.style.minHeight = '100vh'
      document.body.style.overscrollBehaviorY = 'auto'
      document.body.style.webkitOverflowScrolling = 'touch'

      // ✅ Add invisible 1px bounce anchor
      const anchor = document.createElement('div')
      anchor.style.height = '1px'
      anchor.style.width = '100%'
      anchor.style.pointerEvents = 'none'
      anchor.style.flexShrink = '0'
      document.body.appendChild(anchor)
    }

    // Initial viewport height calculation
    updateViewportHeight()

    // Add event listeners for viewport updates
    window.addEventListener('resize', updateViewportHeight)
    window.addEventListener('orientationchange', () => {
      setTimeout(updateViewportHeight, 100)
    })

    // Inject Google Analytics script only if it hasn't been added
    if (
      !document.querySelector(
        `script[src*="gtag/js?id=${GA_MEASUREMENT_ID}"]`
      )
    ) {
      const script = document.createElement('script')
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
      script.async = true
      document.head.appendChild(script)

      script.onload = () => {
        ;(window as any).dataLayer = (window as any).dataLayer || []
        function gtag(...args: any[]) {
          ;(window as any).dataLayer.push(args)
        }
        gtag('js', new Date())
        gtag('config', GA_MEASUREMENT_ID)
      }
    }

    return () => {
      window.removeEventListener('resize', updateViewportHeight)
      window.removeEventListener('orientationchange', updateViewportHeight)
    }
  }, [])

  return (
    <React.StrictMode>
      <App />
    </React.StrictMode>
  )
}

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Failed to find the root element')

// Mount the app
createRoot(rootElement).render(<Root />)
